import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Modal, ModalActions, ModalContent, ModalTitleInverse } from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useTotalByCurrency from '@/utils/useTotalByCurrency';
import { TakerMarketGroupType } from '../data/useGroupByPricingType';
import { getDivisionName } from '../features/homepage/marketTable/columns';
import InvoiceEditLoadingModal from './OfferBuilder/EditInvoices/InvoiceEditLoadingModal';
import useInvoiceEdits, { EditError } from './OfferBuilder/EditInvoices/useInvoiceEdits';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './Table';

interface IncludeEligibleInvoicesModalProps {
  /**
   * Function to close the modal
   */
  onClose: () => void;
  /**
   * Whether the modal is open
   */
  open: boolean;
  /**
   * This indicates the tab the alert is for
   */
  tab: TakerMarketGroupType | 'buyerControls';
  /**
   * The taker markets with eligible invoices to include
   */
  takerMarkets: TakerMarket[];
}

const IncludeEligibleInvoicesModal = ({ onClose, open, tab, takerMarkets }: IncludeEligibleInvoicesModalProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { asCurrency, asNumber } = useLocaleFormat();
  const { selectedCurrency } = useCurrencyContext();
  const takerMarketsWithExclusions = useMemo(() => {
    const filtered = takerMarkets.filter((tm) => tm.takerExcludedInvoiceAmount > 0);
    return filtered.sort((a, b) => {
      const diff = b.takerExcludedInvoiceAmount - a.takerExcludedInvoiceAmount;

      if (diff !== 0) {
        return diff;
      }

      const divisionNameA = getDivisionName(a);
      const divisionNameB = getDivisionName(b);
      return divisionNameB.localeCompare(divisionNameA);
    });
  }, [takerMarkets]);
  const [selectedTakerMarkets, setSelectedTakerMarkets] = useState<TakerMarket[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [errors, setErrors] = useState<EditError[]>([]);
  const { totalByCurrency } = useTotalByCurrency();

  const totalInvoiceCount = selectedTakerMarkets.reduce((sum, tm) => sum + tm.takerExcludedInvoiceCount, 0);
  const totalAmount = totalByCurrency(
    selectedTakerMarkets.map((tm) => ({
      amount: tm.takerExcludedInvoiceAmount,
      fromCurrency: tm.currency,
      toCurrency: selectedCurrency,
    }))
  );
  const allSelected =
    selectedTakerMarkets.length === takerMarketsWithExclusions.length && takerMarketsWithExclusions.length > 0;
  const someSelected =
    selectedTakerMarkets.length > 0 && selectedTakerMarkets.length < takerMarketsWithExclusions.length;

  const { handleIncludeAllInvoices } = useInvoiceEdits({
    onLoadingChange: (isLoading) => {
      setLoading(isLoading);
      if (!isLoading) {
        setProgress(null);
      }
    },
    onProgressChange: (current, total) => setProgress({ current, total }),
    onError: (editErrors) => setErrors(editErrors),
    subscribeToMarketStats: true,
  });

  useEffect(() => {
    if (open) {
      setSelectedTakerMarkets(takerMarketsWithExclusions);
    }
  }, [open, takerMarketsWithExclusions]);

  const handleToggleDivision = (takerMarket: TakerMarket) => {
    setSelectedTakerMarkets((prev) => {
      const isSelected = prev.some((tm) => tm.id === takerMarket.id);
      if (isSelected) {
        return prev.filter((tm) => tm.id !== takerMarket.id);
      }

      return [...prev, takerMarket];
    });
  };

  const handleSelectAll = () => {
    if (selectedTakerMarkets.length === takerMarketsWithExclusions.length) {
      setSelectedTakerMarkets([]);
    } else {
      setSelectedTakerMarkets(takerMarketsWithExclusions);
    }
  };

  const handleApply = async () => {
    if (selectedTakerMarkets.length === 0) {
      return;
    }

    trackEnterpriseEvent('invoices::include-excluded::submitted');
    setErrors([]);
    await handleIncludeAllInvoices(selectedTakerMarkets);

    if (errors.length === 0) {
      onClose();
    }
  };

  const handleCloseAfterError = () => {
    setErrors([]);
    setLoading(false);
    onClose();
  };

  return (
    <>
      <Modal className="max-w-[648px]" disableOutsideClickClose={loading} onClose={onClose} open={open && !loading}>
        <ModalTitleInverse>
          <div className="w-full">
            <div>{t('globalActions.includeExcludedInvoices')}</div>
            <div className="text-sm font-normal text-text-secondary">
              {tab === 'buyerControls'
                ? takerMarketsWithExclusions[0]?.makerOrganizationName
                : tab === 'NAME_YOUR_RATE'
                ? t('core.nameYourRate')
                : tab === 'BENCHMARK'
                ? t('core.variableRate')
                : tab === 'FIXED'
                ? t('core.fixedRate')
                : ''}
            </div>
          </div>
        </ModalTitleInverse>
        <ModalContent className="p-0">
          <div className="sticky top-0 z-10 flex w-full items-center justify-between border-y bg-canvas px-6 py-3">
            <div>
              <div className="font-medium text-text-primary">{t('core.total')}</div>
              <div className="text-sm font-normal text-text-secondary">
                {t('core.divisionsSelectedCount', {
                  selectedCount: asNumber({ value: selectedTakerMarkets.length }),
                  totalCount: asNumber({ value: takerMarketsWithExclusions.length }),
                })}
              </div>
            </div>
            <div className="text-right">
              <div className="font-semibold tabular-nums text-text-primary">
                {asCurrency(totalAmount, selectedCurrency)}
              </div>
              <div className="text-sm text-text-secondary">
                {t('core.invoiceWithCountLowercase', {
                  formattedCount: asNumber({ value: totalInvoiceCount }),
                  count: totalInvoiceCount,
                })}
              </div>
            </div>
          </div>
          <div className="space-y-6 p-6">
            <p>{t('taker.includeExcludedInvoicesMessage')}</p>
            <div className="overflow-hidden rounded-lg border border-stroke">
              <div className="overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allSelected}
                          color="primary"
                          indeterminate={someSelected}
                          onChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead className="max-w-56">{t('core.division')}</TableHead>
                      <TableHead className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span>{t('core.excludedAmount')}</span>
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {takerMarketsWithExclusions.map((takerMarket) => {
                      const isSelected = selectedTakerMarkets.some((tm) => tm.id === takerMarket.id);

                      return (
                        <TableRow key={takerMarket.id}>
                          <TableCell className="w-10">
                            <Checkbox
                              checked={isSelected}
                              color="primary"
                              onChange={() => handleToggleDivision(takerMarket)}
                            />
                          </TableCell>
                          <TableCell className="max-w-56 truncate">
                            <div className="space-y-0.5">
                              <div className="truncate font-medium">{getTakerMarketName(takerMarket)}</div>
                              <div className="truncate text-sm text-text-secondary">
                                {getTakerMarketDivisionTitle(takerMarket)?.content}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="space-y-0.5">
                              <div className="font-medium">
                                {asCurrency(takerMarket.takerExcludedInvoiceAmount, takerMarket.currency)}
                              </div>
                              <div className="text-sm text-text-secondary">
                                {t('core.invoiceWithCountLowercase', {
                                  formattedCount: asNumber({ value: takerMarket.takerExcludedInvoiceCount }),
                                  count: takerMarket.takerExcludedInvoiceCount,
                                })}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </ModalContent>
        <ModalActions>
          <Button onClick={onClose} variant="secondary">
            {t('core.cancel')}
          </Button>
          <Button disabled={selectedTakerMarkets.length === 0} onClick={handleApply} variant="cta">
            {t('core.includeInvoices')}
          </Button>
        </ModalActions>
      </Modal>
      <InvoiceEditLoadingModal
        errors={errors}
        onClearErrors={() => setErrors([])}
        onClose={handleCloseAfterError}
        open={loading}
        progress={progress}
      />
    </>
  );
};

export default IncludeEligibleInvoicesModal;
